/**
 * @author yangtianyu01@corp.netease.com
 * @description do not modify this file
 */

import { MutableRefObject, useEffect, useMemo, useRef, useState } from 'react';
import Bridge from './Bridge';

export type BasicTarget<T = HTMLElement> = (() => T | null) | T | null | MutableRefObject<T | null | undefined>;

type TargetElement = HTMLElement | Element | Document | Window;

export function getTargetElement<T = TargetElement>(target?: BasicTarget<T>, defaultElement?: T): T | undefined | null {
  if (!target) {
    return defaultElement || null;
  }
  let targetElement: T | undefined | null;
  if (typeof target === 'function') {
    // @ts-ignore
    targetElement = target();
  } else if ('current' in target) {
    targetElement = target.current;
  } else {
    targetElement = target;
  }
  return targetElement;
}

function urlPaser(url: string) {
  let parser: any = document.createElement('a');
  parser.href = url;
  const result = {
    protocol: parser.protocol,
    host: parser.host,
    hostname: parser.protocol,
    port: parser.port,
    pathname: parser.pathname,
    hash: parser.hash,
    search: parser.search,
    origin: parser.origin,
    href: parser.href,
  };
  parser = null;
  return result;
}

export const useIframeBridge = (iframe: BasicTarget<HTMLIFrameElement>, defs: any[]) => {
  const iframeBridge = useRef<Bridge | null>(null);
  const iframeListenerFunc = useRef<(message: MessageEvent) => any>(() => {});
  const [timer, setTimer] = useState(1);
  const iframeDom = useMemo(() => getTargetElement(iframe), [timer]);
  useEffect(() => {
    setTimeout(() => {
      setTimer(t => t + 1);
    }, 0);
  }, [iframe, ...defs]);

  const bridge = useMemo(() => {
    if (iframeDom?.contentWindow) {
      const { origin } = urlPaser(iframeDom.src);
      iframeBridge.current = new Bridge({
        onMessage(fn: (mes: string) => void): any {
          function bind(message: MessageEvent) {
            if (message.origin === origin) {
              fn(message.data);
            }
          }
          iframeListenerFunc.current && window.removeEventListener('message', iframeListenerFunc.current, true);
          window.addEventListener('message', bind, true);
          iframeListenerFunc.current = bind;
          return function remove() {
            window.removeEventListener('message', bind, true);
          };
        },
        send: message => {
          if (iframeDom?.contentWindow) {
            iframeDom?.contentWindow.postMessage(message, '*');
          }
        },
      });
    }
    return iframeBridge.current;
  }, [iframeDom?.contentWindow]);

  useEffect(
    () => () => {
      iframeBridge.current?.destroyMessage();
    },
    [],
  );
  return bridge;
};
